import { useEffect, useRef, useCallback } from "react";
import { useSocket } from "./useSocket";
import useTimeoutApiCall, { TEN_MINUTES_MS, TWENTY_MINUTES_MS } from "./useTimeoutApiCall";
import { getCompletedCount, getResultKey } from "./socketFallback.utils";
import designService from "api/design/design.service";
import { httpStatusOf } from "utils/httpStatusOf";

// Constants
export const TWO_MINUTES_MS = 2 * 60 * 1000;
// Canonical polling cadence (REF-5923): after an operation is sent, wait 20 s (WS-only window)
// before falling back to polling, then poll every 5 s. Applied universally across all WS-fallback
// jobs so the fallback recovers a dropped completion frame within ~one short poll interval.
export const TWENTY_SECONDS_MS = 20 * 1000;
export const FIVE_SECONDS_MS = 5 * 1000;
// Slower per-poll cadence for long-running, low-urgency jobs (e.g. production-details /
// cost extraction) that opt out of the universal 20 s/5 s cadence via an explicit
// pollingInterval. Kept as a named constant so those call sites read intentionally.
export const THIRTY_SECONDS_MS = 30 * 1000;
// Grace window for a missing status row (HTTP 404) inside a caller's customStatusCheck (REF-5923).
// The pose/background upload workers INSERT the status row only after Cloud Tasks delivers the
// enqueued job (POST returns a unique_id immediately, before any row exists), so a 404 right after
// submit is usually queue latency, not a real failure. Now that polling starts at 20 s (was 2 min),
// a caller should treat 404s as "still pending" for this window before failing fast — otherwise a
// slow queue false-fails a healthy job. A genuinely dropped job still errors once the window lapses.
export const MISSING_ROW_GRACE_MS = 60 * 1000;
// Per-request ceiling for one status poll (REF-6539). `RefabricProURL` has no axios timeout and
// useTimeoutApiCall only checks its deadline BEFORE awaiting the request, so a single hung GET used to
// stall the whole recovery chain indefinitely on a mounted, visible node. On timeout the poll is treated
// as "still pending" and the next iteration fires at the normal interval.
export const STATUS_POLL_TIMEOUT_MS = 15 * 1000;
/** Minimum spacing between reconnect/online/focus-triggered reconciliation checks (REF-6539). */
export const RECONCILE_COOLDOWN_MS = 2 * 1000;

const POLL_TIMEOUT = Symbol("poll_timeout");
const withPollTimeout = <T>(p: Promise<T>, ms: number): Promise<T | typeof POLL_TIMEOUT> =>
  new Promise((resolve, reject) => {
    const t = setTimeout(() => resolve(POLL_TIMEOUT), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      }
    );
  });

// Re-export TEN_MINUTES_MS from useTimeoutApiCall for backwards compatibility
export { TEN_MINUTES_MS };

interface UseSocketFallbackOptions {
  /** Array of design_types this operation expects (e.g., ["design", "pattern"]) */
  designTypes: readonly string[];
  /** Callback when operation succeeds via POLLING (not socket - socket is handled by global status listeners) */
  onSuccess?: (data: any) => void;
  /** Callback when operation fails via POLLING */
  onError?: (error: any) => void;
  /** Callback for intermediate progress messages (status 0 with partial results) */
  onProgress?: (data: any) => void;
  /** Callback when max polling duration is exceeded without resolution */
  onTimeout?: () => void;
  /** Whether the fallback monitoring is currently active */
  isActive: boolean;
  /** Maximum polling duration after fallback starts (default 20 minutes) */
  maxPollingDuration?: number;
  /** Time to wait before starting fallback polling (default 20 seconds — REF-5923) */
  waitDuration?: number;
  /** Polling interval (default 5 seconds — REF-5923) */
  pollingInterval?: number;
  /**
   * Custom status check function for polling.
   * Should return { status: number, data?: any } where status 1 = success, 99 = error
   * If not provided, uses designService.getDesignStatus
   */
  customStatusCheck?: (id: string) => Promise<{ status: number; type?: string; data?: any }>;
  /**
   * When true, startMonitoring fires one immediate pollStatus() call alongside the normal wait timer.
   * If the job is already done, it resolves instantly (cleanup cancels the timer).
   * If still pending, the timer proceeds normally.
   */
  immediateCheck?: boolean;
  /** When true, also call onSuccess/onError for socket messages (not just polling) */
  handleSocketResult?: boolean;
  /**
   * Opt-in (default false). When true, an ERROR-terminal message in a multi-result
   * flow (expected > 1) is treated as ONE item failing rather than aborting the whole
   * batch: the failure is counted toward completion, the monitor stays alive for the
   * remaining items, and the job finalizes once every item has reported — surfacing the
   * successful results (erroring only if ALL items failed). Leave false for single-result
   * flows and for multi-result callers whose backend emits a single whole-job error
   * (which must still fail-fast). Only enable when the backend sends one terminal
   * message PER item (e.g. studio pose edit).
   */
  continueOnItemError?: boolean;
  /**
   * Optional extra gate on a socket frame that already matched the monitored id + `designTypes`.
   * Return false to IGNORE the frame: it is not consumed, counted, surfaced or allowed to end
   * monitoring. Needed where the id is shared — Studio monitors by `project_id`, so without it a
   * frame for another product or another edit on the same project "completed" the open edit and
   * the real frame then found no one listening. Read through a ref, so an inline function does not
   * restart monitoring; omit it to keep the id + type match alone.
   */
  socketMessageFilter?: (message: any) => boolean;
}

interface UseSocketFallbackReturn {
  /**
   * Start monitoring for a specific design_id.
   * Pass expectedResultCount > 1 for multi-result flows (e.g. multi-pose edit).
   * The hook will only treat the operation as complete when the observed
   * image count (from WS payload or polling response) reaches that number.
   */
  startMonitoring: (designId: string, expectedResultCount?: number) => void;
  /** Stop monitoring (called automatically on unmount) */
  stopMonitoring: () => void;
  /** Whether currently in fallback polling mode */
  isPolling: boolean;
}

/**
 * Hook that monitors socket for operation completion with fallback polling.
 *
 * Flow:
 * 1. Call startMonitoring(designId) after starting an operation
 * 2. Wait for socket message for 20 seconds (REF-5923)
 * 3. If no socket response, start polling every 5 seconds (REF-5923)
 * 4. Stop immediately when socket message arrives
 * 5. Cleanup on unmount
 */
export const useSocketFallback = ({
  designTypes,
  onSuccess,
  onError,
  onProgress,
  onTimeout,
  isActive,
  maxPollingDuration = TWENTY_MINUTES_MS,
  waitDuration = TWENTY_SECONDS_MS,
  pollingInterval = FIVE_SECONDS_MS,
  customStatusCheck,
  immediateCheck,
  handleSocketResult = false,
  continueOnItemError = false,
  socketMessageFilter,
}: UseSocketFallbackOptions): UseSocketFallbackReturn => {
  const { lastMessage, markDesignCompleted, isConnected } = useSocket();
  const { startRecursiveCall, stopRecursiveCalls } = useTimeoutApiCall(maxPollingDuration);

  // Refs for state that doesn't need re-renders
  const designIdRef = useRef<string | null>(null);
  const waitTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef<boolean>(false);
  const socketReceivedRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(true);
  // Expected total number of results for multi-result operations (e.g. multi-pose edit).
  // 1 for single-result flows; set via startMonitoring(id, count).
  const expectedResultCountRef = useRef<number>(1);
  // Counts unique terminal matches surfaced for the current monitoring session.
  // Used as a fallback when the payload shape doesn't expose a cumulative count
  // (e.g. Studio pose-edit ships one WS message per pose with a distinct image_url).
  const terminalCountRef = useRef<number>(0);
  const seenResultKeysRef = useRef<Set<string>>(new Set());
  // Highest cumulative completed count seen for the current job (see applyStatusResult).
  const progressHighWaterRef = useRef<number>(0);
  // Counts items in a multi-result batch that terminated with an ERROR. A single
  // failed sub-result (e.g. one pose of a multi-pose / multi-prompt edit) must NOT
  // fail-fast the whole batch — it is counted toward completion so the job finalizes
  // once every item is accounted for (success + failure), surfacing the successes.
  const failedCountRef = useRef<number>(0);
  // Monotonic monitoring-session token (REF-6539). A poll captures it before its await and bails if the
  // session moved on (stopMonitoring / a new startMonitoring / a terminal socket frame) — a stale
  // response for job A must never settle, stop or reschedule job B, and must never fire callbacks after
  // the socket already won the race. `[SELECTION_ANCHOR_BY_IDENTITY]`: comparing design ids is not
  // enough — successive studio edits monitor the same `project_id`, so A and B are literally the same
  // id; this token distinguishes the RUN, which the id cannot. Silent when it goes wrong.
  const monitorSessionRef = useRef<number>(0);
  // Reconnect reconciliation (REF-6539 gap #2): a job whose completion was delivered while THIS tab's
  // socket was down (frame went live to a sibling tab, or nowhere) is recovered by asking the durable
  // status once, immediately, when the socket comes back / the tab returns — instead of waiting for the
  // next 5 s poll tick or, worse, for a 20 s WS-only window that is still pending. One request per
  // trigger burst: `online` + `focus` + `visibilitychange` + reconnect commonly fire together.
  // ONE status request in flight per monitoring session, whichever entry point issued it (regular chain,
  // immediate check, or reconciliation). A trigger that arrives while a check is in flight or inside the
  // cooldown is not dropped: it is remembered and replayed once the gate opens, so a reconnect that lands
  // 1 s after a focus-check that read "pending" still gets its follow-up.
  const checkInFlightRef = useRef<boolean>(false);
  const reconcilePendingRef = useRef<boolean>(false);
  const reconcileTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastReconcileAtRef = useRef<number>(0);
  const prevConnectedRef = useRef<boolean | undefined>(isConnected);
  // Forward ref so pollStatus (declared first) can hand a remembered trigger back to reconcileNow.
  const runPendingReconcileRef = useRef<() => void>(() => undefined);

  // Ref to access latest lastMessage inside startMonitoring (avoids stale closure)
  const lastMessageRef = useRef(lastMessage);

  // Track the last consumed message to prevent stale re-matching
  const lastConsumedMessageRef = useRef<any>(null);

  // REF-6872 (D5): the frame already retained in `lastMessage` when a session starts belongs to the
  // catch-up alone (REF-6539) — whether the catch-up consumed it, surfaced it as progress or deliberately left
  // it to the caller. The listener effect only takes frames that arrive after the session started, so a host
  // re-render (inline `designTypes`, an `isActive` flip) can neither re-consume a previous run's frame (R4) nor
  // disarm polling on a terminal frame the catch-up left alone (R18). Compared by identity: a redelivered copy
  // that arrives later is a new frame.
  const retainedAtStartRef = useRef<any>(null);
  // The catch-up is gated on `isActive`, and many hosts flip it in the same handler that calls
  // startMonitoring, so the start can see a stale `false`. The catch-up then still owes the retained frame
  // its one evaluation: the listener applies it once, when the hook becomes active for that session.
  const catchUpOwedRef = useRef<boolean>(false);

  // Refs for callbacks to avoid stale closures
  const socketMessageFilterRef = useRef(socketMessageFilter);
  socketMessageFilterRef.current = socketMessageFilter;
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onProgressRef = useRef(onProgress);
  const onTimeoutRef = useRef(onTimeout);

  // Update callback refs when they change
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    onProgressRef.current = onProgress;
    onTimeoutRef.current = onTimeout;
    lastMessageRef.current = lastMessage;
  }, [onSuccess, onError, onProgress, onTimeout, lastMessage]);

  // Cleanup function
  const cleanup = useCallback(() => {
    monitorSessionRef.current += 1;
    // Reconciliation bookkeeping belongs to the session that just ended: an obsolete in-flight request
    // must not gate the next job, and a remembered trigger for the old job must not fire for the new one.
    checkInFlightRef.current = false;
    reconcilePendingRef.current = false;
    lastReconcileAtRef.current = 0;
    if (reconcileTimerRef.current) {
      clearTimeout(reconcileTimerRef.current);
      reconcileTimerRef.current = null;
    }
    if (waitTimerRef.current) {
      clearTimeout(waitTimerRef.current);
      waitTimerRef.current = null;
    }
    stopRecursiveCalls();
    isPollingRef.current = false;
    socketReceivedRef.current = false;
  }, [stopRecursiveCalls]);

  // Applies one status response for `currentDesignId` issued under `session`. Returns true to stop
  // polling, false to continue. Called inline by pollStatus, and again for a SLOW reply that the
  // scheduler already gave up waiting for (see pollStatus) — the stale guards below decide whether
  // that late answer still belongs to the job being monitored.
  const applyStatusResult = useCallback(
    (currentDesignId: string, session: number, result: { status: number; type?: string; responseData: any }): boolean => {
      const { status, type, responseData } = result;

      // Guard against unmount during the async call — do not invoke callbacks if unmounted.
      if (!isMountedRef.current) {
        return true;
      }

      // Stale response: monitoring moved on (stopped, restarted for another id, or the socket already
      // resolved this job) while the request was in flight. Do nothing — and do not touch the new session.
      if (session !== monitorSessionRef.current || designIdRef.current !== currentDesignId || socketReceivedRef.current) {
        console.log("[useSocketFallback] Ignoring stale poll response", { designId: currentDesignId });

        return true;
      }

      // Success via polling
      if (status === 1 || type === "success") {
        const expected = expectedResultCountRef.current;
        const payloadCount = getCompletedCount(responseData);

        // For payloads that don't expose a cumulative count (e.g. Studio), count
        // unique terminal matches ourselves so multi-result flows can still gate
        // completion on the expected total. Skip emission for results we've
        // already surfaced via the socket (prevents duplicate thumbnails when
        // WS + polling both report the same item).
        let isDuplicateResult = false;
        if (payloadCount === null && expected > 1) {
          const key = getResultKey(responseData);
          if (key !== null && seenResultKeysRef.current.has(key)) {
            isDuplicateResult = true;
            console.log("[useSocketFallback] Duplicate result via polling — skipping emission", { key });
          } else {
            if (key !== null) seenResultKeysRef.current.add(key);
            terminalCountRef.current += 1;
          }
        }

        const completed = payloadCount ?? terminalCountRef.current;

        // Cumulative snapshots (payloads that carry their own completed count) must never move
        // backwards: a slow reply that outlasted the 15 s ceiling is still applied when it lands, so an
        // OLDER snapshot can arrive after a NEWER one. Consumers replace their displayed list from each
        // progress callback (Photoshoots), so a regressing count would hide images already shown.
        if (payloadCount !== null && expected > 1) {
          if (payloadCount < progressHighWaterRef.current) {
            console.log("[useSocketFallback] Ignoring out-of-order partial snapshot", { completed: payloadCount, highWater: progressHighWaterRef.current });

            return false;
          }
          progressHighWaterRef.current = payloadCount;
        }

        // Multi-result flows: the backend may flip status to 1 between batches of a
        // larger job (observed with multi-pose edit). Treat as progress — keep polling
        // until every item is accounted for. Count failures already seen via the socket
        // path (`failedCountRef`) toward the total so a failed item doesn't keep us
        // polling forever waiting on a result the backend will never produce.
        if (expected > 1 && completed + failedCountRef.current < expected) {
          if (!isDuplicateResult) {
            console.log("[useSocketFallback] Partial success via polling — keeping monitor alive", {
              completed,
              failed: failedCountRef.current,
              expected,
            });
            onProgressRef.current?.(responseData);
          }

          return false;
        }

        if (isDuplicateResult) {
          return false;
        }

        console.log("[useSocketFallback] Success received via polling", { completed, expected });
        // Mark design as completed to prevent global status listeners from double-handling
        markDesignCompleted(currentDesignId);
        onSuccessRef.current?.(responseData);
        cleanup();
        designIdRef.current = null;

        return true;
      }

      // Error via polling
      if (status === 99 || type === "failed" || type === "error") {
        console.log("[useSocketFallback] Error received via polling");
        onErrorRef.current?.(responseData);
        cleanup();
        designIdRef.current = null;

        return true;
      }

      // Continue polling
      return false;
    },
    [cleanup, markDesignCompleted]
  );

  // Polling function - returns true to stop, false to continue
  const pollStatus = useCallback(async (): Promise<boolean> => {
    const currentDesignId = designIdRef.current;

    // Stop if no design_id, socket already received, or component unmounted
    if (!currentDesignId || socketReceivedRef.current || !isMountedRef.current) {
      return true;
    }

    const session = monitorSessionRef.current;

    // Another status request for this session is still outstanding → don't stack a second one; the
    // scheduler keeps its cadence and a wake-up trigger is remembered by reconcileNow.
    if (checkInFlightRef.current) {
      return false;
    }
    checkInFlightRef.current = true;
    const release = () => {
      // An obsolete request's completion must never open (or close) a NEWER session's gate.
      if (session !== monitorSessionRef.current) return;
      checkInFlightRef.current = false;
      if (reconcilePendingRef.current) runPendingReconcileRef.current();
    };

    try {
      // Use custom status check if provided, otherwise use default design service
      const request: Promise<{ status: number; type?: string; responseData: any }> = customStatusCheck
        ? customStatusCheck(currentDesignId).then((result) => ({ status: result.status, type: result.type, responseData: result.data || result }))
        : designService.getDesignStatus(currentDesignId).then((response) => ({ status: response.responseData?.status, type: response.responseData?.type, responseData: response.responseData }));

      const result = await withPollTimeout(request, STATUS_POLL_TIMEOUT_MS);
      if (result === POLL_TIMEOUT) {
        console.log("[useSocketFallback] Status poll timed out — will retry at the next interval", { designId: currentDesignId });
        // The scheduler moves on (a hung request must not freeze the chain), but the answer is NOT thrown
        // away: a slow reply — a large payload, a slow BE — is applied when it lands, under the same
        // stale guards as an on-time one. Several consumers do work inside their check (the Studio
        // result cursor, the magic-mask download, JOOR progress); dropping a late reply lost that work.
        request.then(
          (late) => {
            applyStatusResult(currentDesignId, session, late);
          },
          () => undefined
        );

        return false;
      }

      return applyStatusResult(currentDesignId, session, result);
    } catch (error) {
      console.error("[useSocketFallback] Polling error", { status: httpStatusOf(error) });

      // Continue polling despite error
      return false;
    } finally {
      release();
    }
  }, [applyStatusResult, customStatusCheck]);

  // Start fallback polling
  const startPolling = useCallback(() => {
    if (socketReceivedRef.current || isPollingRef.current || !isMountedRef.current) {
      return;
    }

    console.log("[useSocketFallback] Starting fallback polling after wait period");
    isPollingRef.current = true;

    startRecursiveCall(
      pollStatus,
      () => {
        // Timeout handler - max polling duration exceeded
        console.log("[useSocketFallback] Max polling duration exceeded");
        cleanup();
        designIdRef.current = null;
        onTimeoutRef.current?.();
      },
      pollingInterval
    );
  }, [startRecursiveCall, pollStatus, cleanup, pollingInterval]);

  // Process a matching socket message (shared by startMonitoring catch-up and useEffect listener)
  const handleMatchedMessage = useCallback(
    (msg: any, matchedDesignId: string) => {
      // Skip if component has unmounted
      if (!isMountedRef.current) return;

      console.log(`[useSocketFallback] Socket message received for design_id: ${matchedDesignId}`, {
        status: msg?.status,
        type: msg?.type,
        design_type: msg?.design_type,
        img_count: msg?.img_count,
        detail_image_count: msg?.design_detail?.image_count,
        detail_images_length: Array.isArray(msg?.design_detail?.images) ? msg.design_detail.images.length : undefined,
        data_length: Array.isArray(msg?.data) ? msg.data.length : undefined,
        first_data_id: Array.isArray(msg?.data) ? msg.data[0]?.id : undefined,
        socketReceivedRef_pre: socketReceivedRef.current,
        isPollingRef_pre: isPollingRef.current,
      });
      lastConsumedMessageRef.current = msg;

      // Determine if this is a terminal status (completed or failed)
      const isErrorTerminal = msg.status === 99 || msg.type === "failed" || msg.type === "error";
      const isSuccessTerminal = msg.status === 1 || msg.type === "completed";
      let isTerminal = isErrorTerminal || isSuccessTerminal;

      // Multi-result flows (e.g. multi-pose / multi-prompt edit): the backend emits one
      // terminal message PER item — success (status:1) or failure (status:99). Gate the
      // whole job on the running total of items accounted for so that:
      //   - a success below the expected total is demoted to "progress" (render it, keep going),
      //   - a SINGLE item's failure does NOT fail-fast the whole batch — it is counted and
      //     the monitor stays alive for the remaining items,
      //   - the job finalizes only once every item has reported. With ≥1 success it finalizes
      //     as success (surfacing those results); only an all-failed batch surfaces as error.
      const expected = expectedResultCountRef.current;
      let isDuplicateResult = false;
      let isBatchItemFailure = false;
      if (expected > 1) {
        if (isSuccessTerminal && !isErrorTerminal) {
          const payloadCount = getCompletedCount(msg) ?? getCompletedCount(msg?.design_detail);

          // For payloads with no cumulative count (e.g. Studio sends one image_url
          // per pose), count unique terminal matches ourselves. Dedup by image_url
          // so the same result redelivered via WS or picked up by fallback polling
          // isn't surfaced to the UI twice.
          if (payloadCount === null) {
            const key = getResultKey(msg);
            if (key !== null && seenResultKeysRef.current.has(key)) {
              isDuplicateResult = true;
              console.log("[useSocketFallback] Duplicate result — skipping emission", { key });
            } else {
              if (key !== null) seenResultKeysRef.current.add(key);
              terminalCountRef.current += 1;
            }
          } else {
            // Cumulative snapshot on the SOCKET path shares one high-water mark with polling: a socket
            // partial showing 2 and a late poll showing 1 (or vice versa) must not move progress
            // backwards. Advance the mark here so the other channel's older snapshot is ignored.
            if (payloadCount < progressHighWaterRef.current) {
              isDuplicateResult = true;
              console.log("[useSocketFallback] Ignoring out-of-order partial snapshot via socket", { count: payloadCount, highWater: progressHighWaterRef.current });
            } else {
              progressHighWaterRef.current = payloadCount;
            }
          }

          const succeeded = payloadCount ?? terminalCountRef.current;
          if (succeeded + failedCountRef.current < expected) {
            console.log("[useSocketFallback] Partial success via socket — keeping monitor alive", {
              succeeded,
              failed: failedCountRef.current,
              expected,
            });
            isTerminal = false;
          }
        } else if (isErrorTerminal && continueOnItemError) {
          // One item in the batch failed. Count it toward completion but DON'T abort
          // the whole job — the other items (succeeded or still generating) survive.
          // Gated by continueOnItemError so callers whose backend emits a single
          // whole-job error keep the fail-fast behavior.
          failedCountRef.current += 1;
          if (terminalCountRef.current + failedCountRef.current < expected) {
            console.log("[useSocketFallback] Batch item failed — keeping monitor alive for remaining items", {
              succeeded: terminalCountRef.current,
              failed: failedCountRef.current,
              expected,
            });
            isTerminal = false;
            isBatchItemFailure = true;
          }
        }
      }

      socketReceivedRef.current = isTerminal;

      if (isTerminal) {
        if (waitTimerRef.current) {
          clearTimeout(waitTimerRef.current);
          waitTimerRef.current = null;
        }

        if (isPollingRef.current) {
          console.log("[useSocketFallback] Stopping polling - terminal socket message received");
          stopRecursiveCalls();
          isPollingRef.current = false;
        }
      }

      if (handleSocketResult && !isDuplicateResult) {
        if (isBatchItemFailure) {
          // One item of a multi-result batch failed; its spinner clears when the batch
          // finalizes. Don't emit — nothing to render, and it's not a whole-job failure.
        } else if (isErrorTerminal) {
          // Single-result failure, or the final item of a batch where NOTHING succeeded
          // → surface as a real error. In an opt-in multi-result batch where some items
          // DID succeed, finalize as success instead (the successful results were already
          // rendered via progress; handleEditComplete ignores the image-less error msg).
          if (continueOnItemError && expected > 1 && terminalCountRef.current > 0) {
            markDesignCompleted(matchedDesignId);
            onSuccessRef.current?.(msg);
          } else {
            onErrorRef.current?.(msg);
          }
        } else if (isTerminal) {
          markDesignCompleted(matchedDesignId);
          onSuccessRef.current?.(msg);
        } else {
          onProgressRef.current?.(msg);
        }
      }

      // Only stop monitoring on terminal statuses. Intermediate messages (status 0
      // or a demoted partial-batch success in a multi-result flow) must keep the
      // monitor alive so later results are still processed. For callers that don't
      // handle socket results directly (handleSocketResult:false), the global status
      // listeners own the UI — the hook just needs to stop when the job is truly done.
      if (isTerminal) {
        cleanup();
        designIdRef.current = null;
      }
    },
    [stopRecursiveCalls, handleSocketResult, markDesignCompleted, cleanup]
  );

  const reconcileNow = useCallback(
    (reason: string) => {
      if (!isMountedRef.current || !designIdRef.current || socketReceivedRef.current) return;
      const now = Date.now();
      const sinceLast = now - lastReconcileAtRef.current;
      if (checkInFlightRef.current || sinceLast < RECONCILE_COOLDOWN_MS) {
        // Coalesce, but never drop: run once more when the in-flight check returns / the cooldown ends.
        reconcilePendingRef.current = true;
        if (!checkInFlightRef.current && !reconcileTimerRef.current) {
          const session = monitorSessionRef.current;
          reconcileTimerRef.current = setTimeout(() => {
            reconcileTimerRef.current = null;
            if (session !== monitorSessionRef.current) return;
            if (reconcilePendingRef.current) runPendingReconcileRef.current();
          }, RECONCILE_COOLDOWN_MS - sinceLast);
        }

        return;
      }
      reconcilePendingRef.current = false;
      lastReconcileAtRef.current = now;
      console.log(`[useSocketFallback] Reconciling job status after ${reason}`, { designId: designIdRef.current });
      pollStatus().catch(() => undefined);
    },
    [pollStatus]
  );
  runPendingReconcileRef.current = () => {
    reconcilePendingRef.current = false;
    reconcileNow("deferred trigger");
  };

  // Socket came back (false → true) while a job is being monitored → check the durable status now.
  useEffect(() => {
    const prev = prevConnectedRef.current;
    prevConnectedRef.current = isConnected;
    if (prev === false && isConnected === true) reconcileNow("socket reconnect");
  }, [isConnected, reconcileNow]);

  // The browser regained network / the tab is back in front → same check (coalesced by the cooldown).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOnline = () => reconcileNow("online");
    const onFocus = () => reconcileNow("focus");
    const onVisible = () => {
      if (document.visibilityState === "visible") reconcileNow("visibilitychange");
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [reconcileNow]);

  // The REF-6539 catch-up decision for a frame that was already in `lastMessage` when the session started.
  // Returns true when it ended monitoring. Shared by startMonitoring and the listener's deferred run (REF-6872).
  const applyCatchUp = useCallback(
    (pending: any, designId: string, types: readonly string[]): boolean => {
      if (!pending || pending === lastConsumedMessageRef.current) return false;
      const isMatchingId = pending.design_id === designId || pending.project_id === designId;
      const isMatchingType = types.includes(pending.design_type);
      // The filter only ever sees a frame that already matched id + type — the contract it is given.
      const isAccepted = isMatchingId && isMatchingType && (!socketMessageFilterRef.current || socketMessageFilterRef.current(pending));
      if (!isAccepted) return false;
      const pendingIsTerminal = pending.status === 1 || pending.status === 99 || pending.type === "completed" || pending.type === "failed" || pending.type === "error";
      console.log(`[useSocketFallback] Caught pre-existing socket message for design_id: ${designId}`, { terminal: pendingIsTerminal, handleSocketResult });
      if (pendingIsTerminal && handleSocketResult) {
        handleMatchedMessage(pending, designId);

        // handleMatchedMessage → cleanup() nulls designIdRef only when the frame really ended the
        // job (a multi-result flow may demote it to progress and keep monitoring).
        return designIdRef.current === null;
      }
      if (!pendingIsTerminal) {
        handleMatchedMessage(pending, designId); // progress — surface it, keep monitoring
      }

      // terminal + handleSocketResult:false → the caller's own listener owns the frame; polling
      // guarantees resolution even if that listener missed it.
      return false;
    },
    [handleMatchedMessage, handleSocketResult]
  );

  // Start monitoring for a design_id
  const startMonitoring = useCallback(
    (newDesignId: string, expectedResultCount?: number) => {
      // Cleanup any previous monitoring
      cleanup();

      // Preserve the dedup/counter state when startMonitoring is re-invoked for
      // the *same* design_id (e.g. dep churn in the host component re-triggers
      // the submit effect). Otherwise already-surfaced results would be treated
      // as new in the next session and emitted to the UI a second time.
      const isSameSession = designIdRef.current === newDesignId;
      designIdRef.current = newDesignId;
      socketReceivedRef.current = false;
      expectedResultCountRef.current = expectedResultCount && expectedResultCount > 1 ? expectedResultCount : 1;
      if (!isSameSession) {
        terminalCountRef.current = 0;
        failedCountRef.current = 0;
        seenResultKeysRef.current = new Set();
        progressHighWaterRef.current = 0;
      }

      console.log(`[useSocketFallback] Started monitoring design_id: ${newDesignId}`, {
        expectedResultCount: expectedResultCountRef.current,
      });

      // Check if a matching socket message already arrived before monitoring started.
      // REF-6539: this catch-up used to `return` unconditionally, which (a) for a NON-terminal frame left
      // the job with no wait timer and no immediate check — no recovery path at all — and (b) for a
      // terminal frame with handleSocketResult:false consumed the frame without telling anyone and then
      // skipped polling, stranding a node whose completion frame beat its own create response. Now:
      // only a terminal frame that this hook is asked to handle itself ends monitoring here; every other
      // case falls through and arms the wait timer + immediate check as a backstop.
      retainedAtStartRef.current = lastMessageRef.current;
      catchUpOwedRef.current = !isActive;
      if (isActive && applyCatchUp(lastMessageRef.current, newDesignId, designTypes)) return;

      // Start wait timer before fallback polling
      waitTimerRef.current = setTimeout(() => {
        console.log(`[useSocketFallback] Wait period ended`, {
          designId: designIdRef.current,
          socketReceivedRef: socketReceivedRef.current,
          willStartPolling: !socketReceivedRef.current,
        });
        if (!socketReceivedRef.current) {
          startPolling();
        }
      }, waitDuration);

      // Fire one immediate poll to catch jobs that completed while the page was away
      if (immediateCheck) {
        console.log(`[useSocketFallback] Immediate check for design_id: ${newDesignId}`);
        pollStatus().catch(() => undefined);
      }
    },
    [cleanup, startPolling, waitDuration, immediateCheck, pollStatus, designTypes, isActive, applyCatchUp]
  );

  // Stop monitoring
  const stopMonitoring = useCallback(() => {
    console.log("[useSocketFallback] Stopping monitoring");
    cleanup();
    designIdRef.current = null;
  }, [cleanup]);

  // Listen for socket messages that arrive AFTER startMonitoring was called
  useEffect(() => {
    const currentDesignId = designIdRef.current;

    // Skip if not active or no design_id being monitored
    if (!lastMessage || !currentDesignId || !isActive) {
      return;
    }
    // Owned by the catch-up (REF-6872): never re-taken by this listener. If the start saw a stale
    // `isActive: false`, the catch-up has not looked at it yet — apply it now, once.
    if (lastMessage === retainedAtStartRef.current) {
      if (catchUpOwedRef.current) {
        catchUpOwedRef.current = false;
        applyCatchUp(lastMessage, currentDesignId, designTypes);
      }

      return;
    }

    // Check if message is for our design_id (or project_id for studio) and design_type
    const isMatchingDesignId = lastMessage.design_id === currentDesignId || lastMessage.project_id === currentDesignId;
    const isMatchingDesignType = designTypes.includes(lastMessage.design_type);

    if (!isMatchingDesignId || !isMatchingDesignType) {
      return;
    }
    // Checked before anything consumes the frame — a rejected frame must leave the monitor untouched.
    if (socketMessageFilterRef.current && !socketMessageFilterRef.current(lastMessage)) {
      return;
    }

    handleMatchedMessage(lastMessage, currentDesignId);
  }, [lastMessage, designTypes, isActive, handleMatchedMessage, applyCatchUp]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      monitorSessionRef.current += 1; // retire the run on unmount so a late reply can never settle it
      designIdRef.current = null;
      if (waitTimerRef.current) {
        clearTimeout(waitTimerRef.current);
      }
      if (reconcileTimerRef.current) {
        clearTimeout(reconcileTimerRef.current);
        reconcileTimerRef.current = null;
      }
      stopRecursiveCalls();
    };
  }, [stopRecursiveCalls]);

  return {
    startMonitoring,
    stopMonitoring,
    isPolling: isPollingRef.current,
  };
};

export default useSocketFallback;
